import { BrowserRouter, Routes, Route } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider } from "@/context/AuthContext";
import { ConfigProvider } from "@/context/ConfigContext";
import { CampaignProvider } from "@/context/CampaignContext";
import { ContentProvider } from "@/context/ContentContext";
import Header from "@/components/Header";
import HolidayBanner from "@/components/HolidayBanner";
import Footer from "@/components/Footer";
import CartDrawer from "@/components/CartDrawer";
import SiteGuard from "@/components/SiteGuard";
import ProtectedRoute from "@/components/ProtectedRoute";
import ScrollRestoration from "@/components/ScrollRestoration";
import useTrackPageView from "@/hooks/useTrackPageView";

import Home from "@/pages/Home";
import Shop from "@/pages/Shop";
import ProductDetail from "@/pages/ProductDetail";
import CustomOrder from "@/pages/CustomOrder";
import Care from "@/pages/Care";
import BlogPost from "@/pages/BlogPost";
import About from "@/pages/About";
import CategoryPage from "@/pages/CategoryPage";
import Workshops from "@/pages/Workshops";
import ResinLab from "@/pages/ResinLab";
import Auction from "@/pages/Auction";
import CheckoutSuccess from "@/pages/CheckoutSuccess";
import CheckoutCancel from "@/pages/CheckoutCancel";
import Maintenance from "@/pages/Maintenance";

import AdminLogin from "@/pages/admin/AdminLogin";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminProducts from "@/pages/admin/AdminProducts";
import AdminOrders from "@/pages/admin/AdminOrders";
import AdminCustomOrders from "@/pages/admin/AdminCustomOrders";
import AdminWorkshops from "@/pages/admin/AdminWorkshops";
import AdminRemakeRequests from "@/pages/admin/AdminRemakeRequests";
import AdminShippingClasses from "@/pages/admin/AdminShippingClasses";
import AdminSettings from "@/pages/admin/AdminSettings";
import AdminContent from "@/pages/admin/AdminContent";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminCampaigns from "@/pages/admin/AdminCampaigns";

import "@/App.css";

function PageViewTracker() {
  useTrackPageView();
  return null;
}

function PublicLayout({ children }) {
  return (
    <>
      <HolidayBanner />
      <Header />
      <main className="min-h-[60vh]">{children}</main>
      <CartDrawer />
      <Footer />
    </>
  );
}

export default function App() {
  return (
    <AuthProvider>
      <ConfigProvider>
        <CampaignProvider>
          <ContentProvider>
          <BrowserRouter>
        <ScrollRestoration />
        <PageViewTracker />
        <Toaster theme="dark" position="bottom-right" />
        <SiteGuard>
          <Routes>
            <Route path="/" element={<PublicLayout><Home /></PublicLayout>} />
            <Route path="/shop" element={<PublicLayout><Shop /></PublicLayout>} />
            <Route path="/category/:slug" element={<PublicLayout><CategoryPage /></PublicLayout>} />
            <Route path="/product/:slug" element={<PublicLayout><ProductDetail /></PublicLayout>} />
            <Route path="/custom-order" element={<PublicLayout><CustomOrder /></PublicLayout>} />
            <Route path="/care" element={<PublicLayout><Care /></PublicLayout>} />
            <Route path="/care/:slug" element={<PublicLayout><BlogPost /></PublicLayout>} />
            <Route path="/about" element={<PublicLayout><About /></PublicLayout>} />
            <Route path="/workshops" element={<PublicLayout><Workshops /></PublicLayout>} />
            <Route path="/resin-lab" element={<PublicLayout><ResinLab /></PublicLayout>} />
            <Route path="/auction" element={<PublicLayout><Auction /></PublicLayout>} />
            <Route path="/checkout/success" element={<PublicLayout><CheckoutSuccess /></PublicLayout>} />
            <Route path="/checkout/cancel" element={<PublicLayout><CheckoutCancel /></PublicLayout>} />
            <Route path="/maintenance" element={<Maintenance />} />

            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin" element={<ProtectedRoute><AdminLayout /></ProtectedRoute>}>
              <Route index element={<AdminDashboard />} />
              <Route path="products" element={<AdminProducts />} />
              <Route path="orders" element={<AdminOrders />} />
              <Route path="custom-orders" element={<AdminCustomOrders />} />
              <Route path="workshops" element={<AdminWorkshops />} />
              <Route path="remakes" element={<AdminRemakeRequests />} />
              <Route path="shipping" element={<AdminShippingClasses />} />
              <Route path="content" element={<AdminContent />} />
              <Route path="analytics" element={<AdminAnalytics />} />
              <Route path="campaigns" element={<AdminCampaigns />} />
              <Route path="settings" element={<AdminSettings />} />
            </Route>
          </Routes>
        </SiteGuard>
          </BrowserRouter>
        </ContentProvider>
        </CampaignProvider>
      </ConfigProvider>
    </AuthProvider>
  );
}
